import { c as _c } from "react/compiler-runtime";
import { textBlock, TextBlockProps, textStyle, TextStyleProps } from '@/uiPrimitives/typography/text';
import { getHyphensPropValue } from './hyphens';
import { JSX, ReactNode } from 'react';
import { classes } from '@/uiPrimitives/base/classes.helpers';
const headingBaseStyle = {
  fontWeight: 'bold',
  capLineHeight: 'tight',
  fontVariantNumeric: 'lining-nums',
  textWrap: 'balance'
} as const satisfies TextStyleProps;
type HeadingLevels = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type HeadingTextBlockProps<THeadingLevel extends HeadingLevels> = {
  children?: ReactNode;
  textBlockProps?: Omit<TextBlockProps, 'capSize'>;
  size?: HeadingLevels;
} & Omit<JSX.IntrinsicElements[THeadingLevel], 'children'>;
const capSizes = {
  h1: '3 | h1',
  h2: '2 | h2',
  h3: '1 | h3',
  h4: '0 | p',
  h5: '0 | p',
  h6: '0 | p'
} as const satisfies Record<HeadingLevels, TextStyleProps['capSize']>;
const hyphensThreshold = {
  h1: 15,
  h2: 20,
  h3: undefined,
  h4: undefined,
  h5: undefined,
  h6: undefined
} as const satisfies Record<HeadingLevels, number | undefined>;
const getHeadingStyle = ({
  children: children,
  size
}: {
  children?: ReactNode;
  size: HeadingLevels;
}) => {
  const hyphens = typeof children === 'string' && hyphensThreshold[size] !== undefined ? getHyphensPropValue({
    text: children,
    threshold: hyphensThreshold[size]
  }) : undefined;
  return {
    ...headingBaseStyle,
    capSize: capSizes[size],
    hyphens,
    hyphenateLimitChars: hyphensThreshold[size] !== undefined ? `${hyphensThreshold[size]}` : undefined
  } as const satisfies TextStyleProps;
};
export const h1TextStyle = (props: TextStyleProps) => textStyle({
  ...getHeadingStyle({
    size: 'h1'
  }),
  ...props
});
export const h1TextBlock = ({
  text,
  ...props
}: Omit<TextBlockProps, 'capSize'> & {
  text: string | undefined;
}) => textBlock({
  ...getHeadingStyle({
    children: text,
    size: 'h1'
  }),
  ...props
});
export const H1TextBlock = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <HeadingTextBlock headingLevel="h1" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const h2TextStyle = ({
  ...props
}: TextStyleProps) => textStyle({
  ...getHeadingStyle({
    size: 'h2'
  }),
  ...props
});
export const h2TextBlock = ({
  text,
  ...props
}: Omit<TextBlockProps, 'capSize'> & {
  text: string | undefined;
}) => textBlock({
  ...getHeadingStyle({
    size: 'h2',
    children: text
  }),
  ...props
});
export const H2TextBlock = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <HeadingTextBlock headingLevel="h2" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const h3TextStyle = (props: TextStyleProps) => textStyle({
  ...getHeadingStyle({
    size: 'h3'
  }),
  ...props
});
export const h3TextBlock = (props: Omit<TextBlockProps, 'capSize'>) => textBlock({
  ...getHeadingStyle({
    size: 'h3'
  }),
  ...props
});
export const H3TextBlock = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <HeadingTextBlock headingLevel="h3" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const h4TextStyle = (props: TextStyleProps) => textStyle({
  ...getHeadingStyle({
    size: 'h4'
  }),
  ...props
});
export const h4TextBlock = (props: Omit<TextBlockProps, 'capSize'>) => textBlock({
  ...getHeadingStyle({
    size: 'h4'
  }),
  ...props
});
export const H4TextBlock = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <HeadingTextBlock headingLevel="h4" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const HeadingTextBlock = t0 => {
  const $ = _c(21);
  let children;
  let className;
  let headingLevel;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      headingLevel,
      size: t1,
      children,
      textBlockProps: t2,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = headingLevel;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
  } else {
    children = $[1];
    className = $[2];
    headingLevel = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const size = t1 === undefined ? headingLevel : t1;
  let T0;
  let t3;
  let t4;
  if ($[7] !== children || $[8] !== className || $[9] !== headingLevel || $[10] !== props || $[11] !== size || $[12] !== t2) {
    const textBlockProps = t2 === undefined ? {} : t2;
    const Tag = headingLevel as HeadingLevels;
    T0 = Tag;
    t3 = props;
    t4 = classes(textBlock({
      ...getHeadingStyle({
        children,
        size
      }),
      ...textBlockProps
    }), className);
    $[7] = children;
    $[8] = className;
    $[9] = headingLevel;
    $[10] = props;
    $[11] = size;
    $[12] = t2;
    $[13] = T0;
    $[14] = t3;
    $[15] = t4;
  } else {
    T0 = $[13];
    t3 = $[14];
    t4 = $[15];
  }
  let t5;
  if ($[16] !== T0 || $[17] !== children || $[18] !== t3 || $[19] !== t4) {
    t5 = <T0 {...t3} className={t4}>{children}</T0>;
    $[16] = T0;
    $[17] = children;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
};